import {RefObject, startTransition, useCallback, useState} from 'react';

import {useIsomorphicLayoutEffect} from '../useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';

interface UseIsObscuredOptions {
  /**
   * If false, the hook will not run the check and will return false.
   * @default true
   */
  enabled?: boolean;
  /**
   * The delay in milliseconds before the check is performed.
   * This is useful to allow other elements to render before the check is performed.
   * @default 300
   * */
  checkDelay?: number;
}

/**
 * Checks if the referenced element is obscured by another element.
 * It works by checking the element at the center of the referenced element.
 *
 * @param elementRef The ref to the element to check.
 * @param options The options for the hook.
 * @returns `true` if the element is obscured, `false` otherwise.
 */
export const useIsObscured = (
  elementRef: RefObject<HTMLElement | null>,
  options: UseIsObscuredOptions = {},
) => {
  const {enabled = true, checkDelay = 300} = options;
  const [isObscured, setIsObscured] = useState(false);

  const checkIsObscured = useCallback(() => {
    try {
      if (!elementRef.current) {
        return;
      }

      const element = elementRef.current;
      const rect = element.getBoundingClientRect();

      // If the element has no size, it's not visible, and thus not obscured.
      if (rect.width === 0 || rect.height === 0) {
        // eslint-disable-next-line no-restricted-syntax -- this usage marks this update as unimportant, valid in this case
        startTransition(() => {
          setIsObscured(false);
        });
        return;
      }

      const points = [
        [rect.left + rect.width / 2, rect.top], // Top-center
        [rect.left, rect.top + rect.height / 2], // Left-center
        [rect.right - 1, rect.top + rect.height / 2], // Right-center
        [rect.left + rect.width / 2, rect.bottom - 1], // Bottom-center
      ];

      for (const [x, y] of points) {
        const topElement = document.elementFromPoint(x!, y!);

        if (topElement && !element.contains(topElement)) {
          setIsObscured(true);
          return;
        }
      }
      setIsObscured(false);
    } catch (error) {
      setIsObscured(false);
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      setIsObscured(false);
      return;
    }

    // A timeout is used to ensure the check runs after the DOM has been updated.
    const timeoutId = setInterval(checkIsObscured, checkDelay);

    return () => {
      clearInterval(timeoutId);
    };
  }, [enabled, checkDelay, checkIsObscured]);

  return isObscured;
};
